import { AppShell, Burger, Button, Group, NavLink, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useCurrentUser, useLogout } from "@/lib/auth/hooks";

type AdminShellProps = {
  children: ReactNode;
};

export function AdminShell({ children }: AdminShellProps) {
  const [opened, { toggle }] = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const logout = useLogout();

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
      navigate("/login", { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Logout failed.";
      notifications.show({
        title: "Logout failed",
        message,
        color: "red",
      });
    }
  };

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 240, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={4}>Pretor Admin</Title>
          </Group>
          <Group gap="sm" wrap="nowrap">
            {user ? (
              <Text size="sm" c="dimmed" visibleFrom="sm">
                {user.email}
              </Text>
            ) : null}
            <Text size="sm" c="dimmed" visibleFrom="md">
              {import.meta.env.VITE_ENVIRONMENT ?? "development"}
            </Text>
            <Button
              variant="light"
              size="compact-sm"
              onClick={() => void handleLogout()}
              loading={logout.isPending}
              data-testid="logout-button"
            >
              Log out
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <NavLink
          component={Link}
          to="/dashboard"
          label="Dashboard"
          active={location.pathname.startsWith("/dashboard")}
        />
        <NavLink
          component={Link}
          to="/users"
          label="Users"
          active={location.pathname.startsWith("/users")}
        />
        <NavLink
          component={Link}
          to="/settings"
          label="Settings"
          active={location.pathname.startsWith("/settings")}
        />
      </AppShell.Navbar>

      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}
